import { useState } from "react";
import { AlertTriangle, Send } from "lucide-react";
import { Modal } from "@/components/ui/Modal";
import type { Announcement } from "../types";

interface Props {
  announcement: Announcement;
  onPublish: (isImportant: boolean) => Promise<unknown>;
  onClose: () => void;
}

/** Acceptance step for a draft — publishing is where a manager decides whether the
 *  announcement is important (pinned to the top + pulsing until recipients read it). */
export function PublishAnnouncementModal({ announcement, onPublish, onClose }: Props) {
  const [isImportant, setIsImportant] = useState(announcement.is_important);
  const [saving, setSaving] = useState(false);

  const handlePublish = async () => {
    setSaving(true);
    try {
      await onPublish(isImportant);
      onClose();
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal open onClose={onClose} title="Publikacja komunikatu" size="md">
      <div className="space-y-4">
        <p className="text-sm text-gray-700 dark:text-gray-300">
          Opublikować komunikat <span className="font-semibold">„{announcement.title}"</span>?
          Od tej chwili zobaczą go wybrani odbiorcy.
        </p>

        <label className="flex items-start gap-2 text-sm text-gray-700 dark:text-gray-300 border border-gray-200 dark:border-gray-700 rounded-lg p-3">
          <input
            type="checkbox"
            checked={isImportant}
            onChange={(e) => setIsImportant(e.target.checked)}
            className="w-4 h-4 mt-0.5"
          />
          <span>
            <span className="inline-flex items-center gap-1.5 font-medium">
              <AlertTriangle size={14} className="text-red-500" /> Oznacz jako ważny
            </span>
            <span className="block mt-0.5 text-xs text-gray-500 dark:text-gray-400">
              Komunikat trafi na samą górę listy i będzie pulsował u odbiorców, dopóki go
              nie przeczytają.
            </span>
          </span>
        </label>

        <div className="flex justify-end gap-2 pt-1">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800"
          >
            Anuluj
          </button>
          <button
            type="button"
            onClick={handlePublish}
            disabled={saving}
            className="inline-flex items-center gap-1.5 px-4 py-2 text-sm rounded-lg bg-[var(--color-primary)] text-white hover:bg-[var(--color-primary-hover)] disabled:opacity-50"
          >
            <Send size={14} /> {saving ? "Publikowanie..." : "Publikuj"}
          </button>
        </div>
      </div>
    </Modal>
  );
}
